import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

export default function Slate() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/projects")
      .then(({ data }) => setProjects(data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="slate"
      data-testid="slate-section"
      className="relative z-10 bg-alabaster py-24 md:py-36 border-t border-[#DCDEE6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-ochre">
              Current Slate
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-6 text-onyx">
              In development <span className="italic text-ochre">&amp;</span> beyond.
            </h2>
          </div>
          <p className="font-sans text-onyx-muted max-w-sm">
            A selection of projects currently moving through the studio at various stages
            of development and production.
          </p>
        </div>

        {loading ? (
          <div data-testid="slate-loading" className="font-mono text-sm text-onyx-muted py-20">
            Loading slate…
          </div>
        ) : projects.length === 0 ? (
          <div data-testid="slate-empty" className="font-serif text-2xl text-onyx-muted py-20">
            The slate is being curated. Check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="slate-grid">
            {projects.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`slate-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.1 }}
                className="group"
              >
                <div className="overflow-hidden aspect-[3/4] border border-[#DCDEE6] mb-5">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-100 group-hover:scale-105 transition-all duration-700 ease-out"
                    />
                  ) : (
                    <div className="w-full h-full bg-surface-warm flex items-center justify-center font-serif text-ochre">
                      {p.title}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ochre">
                    {p.status}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-onyx-muted">
                    {p.category} {p.year && `· ${p.year}`}
                  </span>
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-onyx mt-2 leading-tight">
                  {p.title}
                </h3>
                <p className="font-sans text-sm text-onyx-muted mt-1">{p.role}</p>
                {p.logline && (
                  <p className="font-sans text-sm leading-relaxed text-onyx-muted mt-3 italic">
                    {p.logline}
                  </p>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
